import { FC, useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../redux/hooks';
import { useEnsuredProjectId } from '../project/projectSelectors';
import {
  IGetUploadedImagesListEntry,
  treditionApi,
  useDeleteDesignImageMutation,
  useGetUploadedDesignerImagesQuery,
} from '../../api/treditionApi';
import { QueryLoader } from '../../loader/QueryLoader';
import { DesignerImageSource, DragContextType, dragStart } from '../drag/dragSlice';
import { Uploader } from '../../upload/Uploader';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { ImagePreview } from '../imagePreview/ImagePreview';
import { toast } from 'react-hot-toast';
import { noop } from '../../lib/noop';
import { SidePanelContentWrapper } from './SidePanelContentWrapper';

export const UploadsPanel: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const projectId = useEnsuredProjectId();
  const [previewEntry, setPreviewEntry] = useState<IGetUploadedImagesListEntry | null>(null);
  const query = useGetUploadedDesignerImagesQuery(projectId);
  const [deleteMutation] = useDeleteDesignImageMutation();

  const onBeginUpload = useCallback(
    (file: File) => {
      return dispatch(
        treditionApi.endpoints.uploadDesignImage.initiate({ projectId, file }),
      ).unwrap();
    },
    [dispatch, projectId],
  );

  const onRequestDelete = useCallback((): Promise<void> => {
    if (!previewEntry) {
      return Promise.resolve();
    }
    const deleteTask = deleteMutation({ projectId, fileId: previewEntry.fileId }).unwrap();
    return toast
      .promise(deleteTask, {
        error: t('DeleteFileError'),
        loading: t('DeleteFileLoading'),
        success: t('DeleteFileSuccess'),
      })
      .then(() => {
        setPreviewEntry(null);
      })
      .catch(noop);
  }, [deleteMutation, projectId, previewEntry, t]);

  return (
    <SidePanelContentWrapper>
      <h2>{t('Uploads')}</h2>

      <Uploader onBeginUpload={onBeginUpload} />

      <QueryLoader query={query}>
        <h3>{t('UploadsHeader')}</h3>
        <ImageWrapper>
          {query.isSuccess &&
            query.data.map((entry) => (
              <UploadThumbnail
                key={entry.fileId}
                entry={entry}
                onClick={() => setPreviewEntry(entry)}
              />
            ))}
        </ImageWrapper>
      </QueryLoader>
      {!!previewEntry && (
        <ImagePreview
          imageSrc={previewEntry.fileUrl}
          onRequestClose={() => setPreviewEntry(null)}
          onRequestDelete={onRequestDelete}
        />
      )}
    </SidePanelContentWrapper>
  );
};

/**
 * Renders a single uploaded image thumbnail that can be dragged onto the canvas.
 *
 * Two things are needed for the native HTML5 drag to work in recent Chromium (a regression broke it
 * in Chrome ~149; Firefox is unaffected):
 *
 * 1. The bytes are loaded via `fetch` and exposed as a same-origin `blob:` object URL rather than
 *    pointing `<img>` straight at the cross-origin blob-storage URL.
 * 2. We hand Chrome an explicit drag-feedback image via `setDragImage` (the already-rendered, small
 *    thumbnail). Letting Chrome generate one from the full-resolution source aborts the drag
 *    immediately (`dragstart` -> instant `dragend`). `setDragImage` requires a same-origin image,
 *    which is why (1) is also necessary.
 */
const UploadThumbnail: FC<{ entry: IGetUploadedImagesListEntry; onClick: () => void }> = ({
  entry,
  onClick,
}) => {
  const dispatch = useAppDispatch();
  const [src, setSrc] = useState<string>(entry.fileUrl);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    fetch(entry.fileUrl)
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(noop);

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [entry.fileUrl]);

  return (
    <img
      src={src}
      alt=""
      draggable
      onClick={onClick}
      onDragStart={(e) => {
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
        dispatch(
          dragStart({
            type: DragContextType.DesignerImage,
            fileId: entry.fileId,
            source: DesignerImageSource.Upload,
          }),
        );
      }}
    />
  );
};

const ImageWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 7px;

  & > img {
    max-height: 110px;
    max-width: 100%;
    border-radius: var(--border-radius);
    box-shadow: 2px 2px 5px var(--box-shadow-color);
    cursor: pointer;
  }

  & > img:hover {
    outline: 5px solid var(--color-cta);
    outline-offset: -2px;
  }
`;
